import { Filter, FilterV2, SearchV2, SortV2 } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageTitle from '../components/PageTitle';
import WorkpaperProceduresTable from '../features/workpaper-procedures/WorkpaperProceduresTable';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { saveFilterV2, saveSortV2 } from '../slices/workpaperFilterSlice';
import { RootState } from '../store';
import ProceduresTabs from '../features/procedures/ProceduresTabs';
import NavBreadcrumbs from '../components/nav-breadcrumbs/NavBreadcrumbs';
import GrowHeightContainer from '../components/GrowHeightContainer';
import { useSearchParams } from 'react-router-dom';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import { WorkpaperProcedure, WorkpaperProcedureStatusEnum } from '../types/workpaper-procedure';
import { isRequiredRole } from '../utilities/is-required-role';
import AddonsSidebar from '../features/workpaper-procedures/addons/AddonsSidebar';
import WorkpaperProcedureNotApplicableConfirmDialog from '../features/workpaper-procedures/WorkpaperProcedureNotApplicableConfirmDialog';
import WorkpaperProcedureActiveConfirmDialog from '../features/workpaper-procedures/WorkpaperProcedureActiveConfirmDialog';
import { useWorkpaperProcedures } from '../queries/use-workpaper-procedures';
import { useWorkpaperProcedureCategories } from '../queries/use-workpaper-procedure-categories';
import { categoriseProcedures, getFirstProcedureId } from '../features/workpaper-procedures/utils';
import WorkpaperProceduresTopActions from '../features/workpaper-procedures/WorkpaperProceduresTopActions';

const statusFilterOptions = [
    { active: false, label: 'Active', value: 'ACTIVE' },
    { active: false, label: 'Checked', value: 'CHECKED' },
    { active: false, label: 'Not Applicable', value: 'NOT_APPLICABLE' },
];

const roleFilterOptions = [
    { active: false, label: 'Preparer', value: 'PREPARER' },
    { active: false, label: 'Reviewer', value: 'REVIEWER' },
    { active: false, label: 'Approver', value: 'APPROVER' },
];

function ProceduresPage(): JSX.Element {
    const { id: workpaperId } = useWorkpaperContext();
    const [search, setSearch] = useState<string | undefined>('');
    const [sort, setSort] = useState<string | undefined>('');
    const [filter, setFilter] = useState<string | undefined>('');
    const [statusFilter, setStatusFilter] = useState<string | undefined>('');
    const [isOpenNotApplicableDialog, setIsOpenNotApplicableDialog] = useState(false);
    const [isOpenActiveDialog, setIsOpenActiveDialog] = useState(false);
    const [selectedProcedures, setSelectedProcedures] = useState<WorkpaperProcedure[]>([]);
    const [collapsedCategories, setCollapsedCategories] = useState<string[]>([]);
    const [query, setQuery] = useSearchParams();
    const { role } = useWorkpaperContext();

    const applicableProcedures = selectedProcedures.filter(
        (p) => p.status !== WorkpaperProcedureStatusEnum.NOT_APPLICABLE && isRequiredRole(role, p.role)
    );
    const notApplicableProcedures = selectedProcedures.filter(
        (p) => p.status === WorkpaperProcedureStatusEnum.NOT_APPLICABLE && isRequiredRole(role, p.role)
    );

    const savedFilters = useSelector((state: RootState) => state.workpaperFilters.value[workpaperId]?.procedures);
    const dispatch = useDispatch();

    useEffect(() => {
        if (savedFilters?.filterQuery || savedFilters?.sortQuery) {
            setQuery((s) => {
                if (s.get('tab')) {
                    const tab = s.get('tab');
                    return `tab=${tab}&${savedFilters.filterQuery || ''}&${savedFilters.sortQuery || ''}`;
                }
                return `${savedFilters.filterQuery || ''}&${savedFilters.sortQuery || ''}`;
            });
        }
    }, []);

    function handleStatusChange(status: string | null): void {
        setStatusFilter(status !== null ? `status=${status}` : undefined);
    }

    function updateSelectedProcedures(procedure: WorkpaperProcedure): void {
        if (selectedProcedures.find((p) => p.id === procedure.id)) {
            setSelectedProcedures(selectedProcedures.filter((p) => p.id !== procedure.id));
            return;
        }
        setSelectedProcedures([...selectedProcedures, procedure]);
    }

    function handleUpdate(): void {
        setIsOpenNotApplicableDialog(false);
        setIsOpenActiveDialog(false);
        setSelectedProcedures([]);
    }

    function handleToggleCollapse(categoryId: string): void {
        if (collapsedCategories.includes(categoryId)) {
            setCollapsedCategories(collapsedCategories.filter((i) => i !== categoryId));
            return;
        }

        setCollapsedCategories([...collapsedCategories, categoryId]);
    }

    const workpaperProceduresQuery = useWorkpaperProcedures(workpaperId, {
        search,
        sort,
        filter: statusFilter ? statusFilter + '&' + filter : filter,
        searchFilters: statusFilter ? 'searchFilters=status' : '',
    });
    const workpaperProcedureCategoriesQuery = useWorkpaperProcedureCategories();

    if (workpaperProceduresQuery.isLoading || workpaperProcedureCategoriesQuery.isLoading) {
        return <></>;
    }

    if (
        workpaperProceduresQuery.isError ||
        !workpaperProceduresQuery.isSuccess ||
        workpaperProcedureCategoriesQuery.isError ||
        !workpaperProcedureCategoriesQuery.isSuccess
    ) {
        return <p>Error!</p>;
    }

    const { workpaperProcedures } = workpaperProceduresQuery.data;
    const { categories } = workpaperProcedureCategoriesQuery.data;

    const { categoryGroups, otherProceduresGroup } = categoriseProcedures(categories, workpaperProcedures, collapsedCategories);
    const firstProcedureId = getFirstProcedureId(categoryGroups, otherProceduresGroup);

    return (
        <div className="flex min-h-full w-full flex-col">
            <div className="flex h-full w-full">
                <div className="w-full flex flex-col h-full relative pr-2">
                    <NavBreadcrumbs />
                    <div className="flex justify-between flex-wrap mb-4">
                        <div className="flex-1">
                            <PageTitle title="Procedures" />
                            <p className="text-gray-600 text-sm max-w-[600px]">
                                View all workpaper procedures. To assist with this, you can search & filter the data to view only the
                                procedures you need to see.
                            </p>
                        </div>
                        <div className=" flex flex-col justify-start items-end gap-1">
                            <WorkpaperProceduresTopActions firstProcedureId={firstProcedureId} />
                            <SecondaryPageActions hidden={selectedProcedures.length === 0} maxActionsCount={2}>
                                <SecondaryPageActions.Button
                                    label={`Mark as not applicable (${applicableProcedures.length})`}
                                    onClick={(): void => setIsOpenNotApplicableDialog(true)}
                                    hidden={applicableProcedures.length === 0}
                                />
                                <SecondaryPageActions.Button
                                    label={`Mark as applicable (${notApplicableProcedures.length})`}
                                    onClick={(): void => setIsOpenActiveDialog(true)}
                                    hidden={notApplicableProcedures.length === 0}
                                />
                            </SecondaryPageActions>
                        </div>
                    </div>

                    <ProceduresTabs onStatusChange={handleStatusChange} />
                    <GrowHeightContainer className="flex gap-4 items-center mt-4">
                        <FilterV2
                            onChange={({ queryString }): void => {
                                setFilter(queryString);
                                dispatch(saveFilterV2({ workpaperId: workpaperId, section: 'procedures', query: queryString }));
                            }}
                        >
                            <FilterV2.ToggleFilter label="Notes" id="notes" prefix="has" />
                            <FilterV2.CheckboxFilter options={roleFilterOptions} id="role" label="Role" />
                            <FilterV2.DateFilter id="updatedAt" label="Last Updated" />
                            {!statusFilter && <FilterV2.CheckboxFilter options={statusFilterOptions} id="status" label="Status" />}
                        </FilterV2>

                        <SearchV2 onChange={setSearch} placeholder="Search procedures..." />
                    </GrowHeightContainer>

                    <section className="mt-4 pb-4">
                        <WorkpaperProceduresTable
                            selectedProcedures={selectedProcedures}
                            otherProceduresGroup={otherProceduresGroup}
                            categoryGroups={categoryGroups}
                            workpaperProcedures={workpaperProcedures}
                            onProcedureChecked={updateSelectedProcedures}
                            onToggleCollapse={handleToggleCollapse}
                        />
                    </section>
                    <WorkpaperProcedureNotApplicableConfirmDialog
                        workpaperId={workpaperId}
                        procedureIds={applicableProcedures.map((p) => p.id)}
                        isOpen={isOpenNotApplicableDialog}
                        onClose={(): void => setIsOpenNotApplicableDialog(false)}
                        onUpdate={handleUpdate}
                    />
                    <WorkpaperProcedureActiveConfirmDialog
                        workpaperId={workpaperId}
                        procedureIds={notApplicableProcedures.map((p) => p.id)}
                        isOpen={isOpenActiveDialog}
                        onClose={(): void => setIsOpenActiveDialog(false)}
                        onUpdate={handleUpdate}
                    />
                </div>
                <AddonsSidebar />
            </div>
        </div>
    );
}
export default ProceduresPage;
